import { useCallback, useContext, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { Countries } from '~/common/constants/countries.enum';
import { UserContext } from '~/components/providers/UserProvider';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { getZipCodeSchema, sanitizeZipCode } from '~/domains/kyc/utils/zipCodeSchemas';

import { getIsZipGateRequired } from '../../../utils/getIsZipGateRequired';

function useEntryZipGate(contest: FullContest | Contest) {
  const { t } = useTranslation('contest');
  const { userDetails } = useContext(UserContext);
  const [zipCode, setZipCode] = useState('');
  const [isZipTermsChecked, setIsZipTermsChecked] = useState(false);
  const countryForZipValidation =
    userDetails?.country === Countries.CA ? Countries.CA : Countries.US;

  const zipSchema = useMemo(
    () => getZipCodeSchema(countryForZipValidation),
    [countryForZipValidation]
  );
  const maxZipLength = countryForZipValidation === Countries.CA ? 7 : 5;

  const isZipGateRequired = getIsZipGateRequired(contest);
  const isZipCodeValid = zipSchema.safeParse(zipCode).success;
  const isZipGateUnlocked = !isZipGateRequired || (isZipCodeValid && isZipTermsChecked);
  const zipCodeError =
    zipCode.length > 0 && !isZipCodeValid
      ? t('common.enterContestModal.enterContest.zipCodeError')
      : undefined;

  const handleZipCodeChange = useCallback(
    (value: string) => setZipCode(sanitizeZipCode(value, countryForZipValidation)),
    [countryForZipValidation]
  );

  return {
    zipCode,
    isZipTermsChecked,
    isZipGateRequired,
    isZipGateUnlocked,
    zipCodeError,
    maxZipLength,
    handleZipCodeChange,
    setIsZipTermsChecked,
  };
}

export default useEntryZipGate;
